import { useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import Calendar from './Calendar.jsx';
import DateInput from './DateInput.jsx';
import { en } from './datepicker.translation.js';
import { validateTranslation, validateDateFormat, zeroPrefix } from './datepicker.helpers.js';

const LdsDatepicker = forwardRef(function LdsDatepicker({
  dateFormat = 'MM/DD/YYYY',
  defaultValue = '',
  disableCalendar = false,
  disabledDates = [],
  disableWeekends = false,
  focusDay,
  hint = '',
  id,
  label = '',
  max,
  min,
  onChange = () => {},
  translation = en,
  startOfWeek = 'sunday',
  className,
  ...rest
}, ref) {
  /*
    This component was built fully custom due to a lack of accessible date picker options
    After an a11y review of <input type="date" /> and other library date pickers such as mui or wc-datepicker got close
    but did not meet all a11y requirements
  */
  const [dateValue, setDateValue] = useState(defaultValue);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handleChangeFromCalendar = (year, month, day) => {
    let newValue = '';
    const prefixDay = zeroPrefix(day);
    const prefixMonth = zeroPrefix(month);
    if (dateFormat === 'MM/DD/YYYY') {
      newValue = `${prefixMonth}/${prefixDay}/${year}`;
    } else if (dateFormat === 'DD/MM/YYYY') {
      newValue = `${prefixDay}/${prefixMonth}/${year}`;
    } else if (dateFormat === 'YYYY-MM-DD') {
      newValue = `${year}-${prefixMonth}-${prefixDay}`;
    }

    setDateValue(newValue);
    onChange(newValue);
  };

  return (
    <div
      className={cn(
        'lds-datepicker-wrapper',
        className
      )}
      {...rest}
    >
      <DateInput
        id={id}
        label={label}
        dateFormat={dateFormat}
        dateValue={dateValue}
        setDateValue={setDateValue}
        onChange={onChange}
        hint={hint}
        isCalendarOpen={isCalendarOpen}
        setIsCalendarOpen={setIsCalendarOpen}
        disableCalendar={disableCalendar}
        translation={translation}
        ref={ref}
      />
      <Calendar
        id={id}
        translation={translation}
        isCalendarOpen={isCalendarOpen}
        setIsCalendarOpen={setIsCalendarOpen}
        focusDay={focusDay}
        dateValue={dateValue}
        onChange={handleChangeFromCalendar}
        disableWeekends={disableWeekends}
        disabledDates={disabledDates}
        min={min}
        max={max}
        dateFormat={dateFormat}
        startOfWeek={startOfWeek}
      />
    </div>
  );
});

LdsDatepicker.propTypes = {
  id: PropTypes.string.isRequired,
  dateFormat: PropTypes.oneOf(['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD']),
  translation: (props, propName, componentName) => validateTranslation(props[propName], componentName),
  min: (props, propName, componentName) => props[propName] && validateDateFormat(props, propName, componentName, '', 'min'),
  max: (props, propName, componentName) => props[propName] && validateDateFormat(props, propName, componentName, '', 'max'),
  disabledDates: PropTypes.arrayOf(validateDateFormat),
  focusDay: (props, propName, componentName) => props[propName] && validateDateFormat(props, propName, componentName, '', 'focusDay'),
  startOfWeek: PropTypes.oneOf(['sunday', 'monday', 'saturday']),
};

export default LdsDatepicker;
